/**
 * Build a clean, displayable Error from a failed fetch Response.
 *
 * Our own endpoints answer with `{"statusCode":403,"statusMessage":"…"}`, and
 * that message is worth showing. Everything in front of them is not: a
 * Cloudflare 504/524, an nginx 502, a WAF block or a captive portal answers with
 * a full HTML document, and the old implementation fell back to the raw body —
 * which is how an entire `<!DOCTYPE html>` page ended up rendered under the URL
 * field. Bodies are now only trusted when they are JSON (or short plain text)
 * *and* survive sanitising; otherwise the user gets the status code plus a
 * written explanation of it.
 *
 * The returned Error carries:
 *   - `message` / `displayMessage` — the safe one-line string to render
 *   - `statusCode`                 — the HTTP status, for callers that branch on it
 *   - `rawBody`                    — the untouched body, for logging only
 *
 * Auto-imported by Nuxt (utils/), so it's available unqualified everywhere.
 */
import { formatHttpError, sanitizeErrorText } from './errorMessage'

export interface DisplayableError extends Error {
  statusCode: number
  displayMessage: string
  rawBody?: string
}

// A message from a body is only believable if it is short. Real error copy is a
// sentence; anything longer is a document that happened to dodge the markup test.
const MAX_BODY_MESSAGE_LENGTH = 400

const pickJsonMessage = (body: any): string => {
  if (!body || typeof body !== 'object') return ''
  const candidates = [
    body.statusMessage,
    body.message,
    body.error,
    body.error_description,
    body.detail,
    body.data?.statusMessage,
    body.data?.message,
    // Some gateways nest the real error: { error: { message: "…" } }
    typeof body.error === 'object' ? body.error?.message : undefined,
  ]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim() !== '') return candidate
  }
  return ''
}

export const responseError = async (res: Response): Promise<DisplayableError> => {
  const status = typeof res?.status === 'number' ? res.status : 0

  // Reading the body can itself fail (connection dropped mid-response, body
  // already consumed). A failure here must never mask the status we do have.
  let text = ''
  try {
    text = await res.text()
  } catch {
    text = ''
  }

  const contentType = (() => {
    try {
      return res.headers?.get('content-type') ?? ''
    } catch {
      return ''
    }
  })()

  const trimmed = text.trim()
  const looksJson = /json/i.test(contentType) || /^[[{]/.test(trimmed)

  let detail = ''
  if (looksJson) {
    try {
      detail = pickJsonMessage(JSON.parse(trimmed))
    } catch {
      // Content-Type lied, or the JSON is truncated — treat it as an opaque body.
      detail = ''
    }
  } else if (/^text\/plain\b/i.test(contentType) && trimmed.length <= MAX_BODY_MESSAGE_LENGTH) {
    // A short text/plain body is usually a genuine message ("Rate limit
    // exceeded"). sanitizeErrorText still has the final say.
    detail = trimmed
  }
  // Anything else — text/html, empty, oversized, unknown — contributes nothing.

  if (detail.length > MAX_BODY_MESSAGE_LENGTH) detail = ''
  // Drop a body message that only restates the status line ("504 Gateway Time-out").
  const withoutStatus = sanitizeErrorText(detail.replace(new RegExp(`^\\s*${status}\\s*[:-]?\\s*`), ''))

  const displayMessage = formatHttpError(status, withoutStatus)

  const error = new Error(displayMessage) as DisplayableError
  error.name = 'HttpError'
  error.statusCode = status
  error.displayMessage = displayMessage
  // Kept off the message on purpose: available to console/Sentry, never rendered.
  if (text) error.rawBody = text.slice(0, 2000)
  return error
}
